import axios from "axios";
import _isEqual from "lodash/isEqual";
import store from "../store/index";

import crypto from "@/utils/encrypt";

import { MessageBox } from "element-ui";
import { message } from "@/utils/message.js";
import { clearLocalStorageKeepSidebarPinned } from "@/utils/storage";
import { eventBus } from "@/main";
import EventCenter from "@/utils/EventCenter";

import teamPrompy from "@/utils/createTeam.js";

import router from "@/router/index"

const { createTeamPrompt, switchTeamPrompt } = teamPrompy;

// WhatsApp 工作台自行按当前语言展示接口失败，跳过此处通用的单语提示和重复弹窗。
const whatsappWorkspaceOwnsErrors = () => router?.currentRoute?.path === "/bot/whatsapp-service";

const handleError = (error) => {
  if (whatsappWorkspaceOwnsErrors()) return;
  let _message = "发生未知错误，请稍后再试。";
  if (axios.isCancel(error)) {
    // console.log("Request canceled:", error.message);
    return;
  }
  if (error.response) {
    // 服务器响应了请求，但状态码不在 2xx 范围内
    _message = handleHttpErrorStatus(error.response.status, error.response.data);
  } else if (error.request) {
    // 请求已发出，但未收到响应
    _message = "网络请求未响应，请检查您的网络连接。";
  } else {
    _message = error.message;
  }
  message({ message: _message, type: "error" });
};

const noTeamHandler = async (msg) => {
  try {
    const val = await createTeamPrompt(msg);
    const insertRes = await axios_("post", "api/pandagpt/v1/team/insert", {
      team_name: val,
    });
    if (insertRes.code === 1) {
      store.commit("authoState/CHANGE_TEAM_INFO", insertRes.data);
    } else {
      message({ message: insertRes.msg, type: "warning" });
    }
  } catch (error) {
    console.error("Error in noTeamHandler:", error);
  }
};

const handleHttpErrorStatus = (status, data) => {
  const messages = {
    400: "请求参数错误",
    401: "未授权，请登录",
    403: "拒绝访问",
    404: "请求地址出错",
    408: "请求超时",
    500: "服务器内部错误",
    501: "服务未实现",
    502: "网关错误",
    503: "服务不可用",
    504: "网关超时",
    505: "HTTP版本不受支持",
  };
  return `code=${status}：${messages[status] || "连接出错"}`;
};

const service = axios.create({
  timeout: 1000 * 60 * 5,
});

let cancelTokenSources = new Map();
/**
 * @type {Map<string, {controller:AbortController; params:Record<string,any>;}>}
 */
let abortControllers = new Map();

// request拦截器
service.interceptors.request.use(
  (config) => {
    const timestamp = Date.now() + 1000 * 60;
    var post_data;
    if (config.method.toLowerCase() === "post") {
      post_data = { timestamp };
      if (config.data instanceof FormData) {
        // FormData 请求：仅文本字段参与签名（后端签名前会剔除 files 字段，二进制内容无法参与 HMAC）
        config.data.forEach((value, key) => {
          if (typeof value === "string") {
            post_data[key] = value;
          }
        });
      } else {
        Object.assign(post_data, config.data);
      }
    } else if (config.method.toLowerCase() === "get") {
      post_data = {
        ...config.params,
        timestamp,
      };
    }
    config.headers["sign"] = crypto.generateSign(post_data);
    config.headers["token"] = localStorage.getItem("token") || "";
    config.headers["timestamp"] = timestamp;
    // console.log(`config.noCancelable`, config.noCancelable);
    if (config.noCancelable) {
      return config;
    }
    const requestKey = `${config.method}:${config.url}`;
    const currentParams = config.params || config.data;
    /*if (cancelTokenSources[requestKey]) {
      const previousParams = cancelTokenSources[requestKey].params;
      const currentParams = config.params || config.data;

      if (_isEqual(previousParams, currentParams)) {
        cancelTokenSources[requestKey].cancel("Canceled due to new request with the same parameters");
      }
    }*/
    if (abortControllers.has(requestKey)) {
      const { controller, params } = abortControllers.get(requestKey);
      if (_isEqual(params, currentParams)) {
        controller.abort("Canceled due to new request with the same parameters");
      }
    }
    /*config.cancelToken = new axios.CancelToken(cancel => {
      cancelTokenSources.set(requestKey, { cancel, params: config.params || config.data });
    });*/

    const controller = new AbortController();
    config.signal = controller.signal;
    abortControllers.set(requestKey, { controller, params: currentParams });

    return config;
  },
  (error) => Promise.reject(error)
);
// 响应拦截器
service.interceptors.response.use(
  (response) => {
    // 清理取消令牌
    const requestKey = `${response.config.method}:${response.config.url}`;
    // cancelTokenSources.delete(requestKey);

    abortControllers.delete(requestKey);
    const { msg, code } = response.data;
    switch (code) {
      case 20009:
        // 20009 主页取消绑定
        MessageBox.alert(msg, "提示", {
          type: "warning",
          confirmButtonText: "确定",
        });
        break;
      case 20008:
        switchTeamPrompt(msg);
        break;
      case 20007:
        noTeamHandler(msg);
        break;
      case 20001:
        clearLocalStorageKeepSidebarPinned();
        store.commit("authoState/CLEAR_AUTH_STATE");
        router.replace("/homepage").catch((err) => console.error(err))
        break;
      case 50003:
        // 套餐限制统一触发根节点弹窗，同时返回响应让原调用方完成自身 loading 收尾。
        eventBus.$emit(EventCenter.openPackageLimitDialog);
        return response;
      case 50005:
        // 账号授权到达 +3 状态，提示封禁
        eventBus.$emit(EventCenter.openPackageLockedDialog);
        return response;
      case 1:
      case 2001: // 无法关联，该主页已关联到其他团队
      case 3001: // 主页绑定机器人或正在监控，请先进行解绑。
      case 30001: // telegram 二次授权验证码
      case 50004: // 手动停止运营机器人
      case 20010: // 手机号已存在
      case 50002: // 套餐购买费用明细发生改变
      case 50001: // 团队余额不足
      case 20013: // 超管没绑定手机号
      case 70002: // facebook授权 该账户已在其他团队进行了部分授权
        return response;
      default:
        // 工作台调用方会检查业务 code 并显示本地化提示，保留响应供其处理。
        if (whatsappWorkspaceOwnsErrors()) return response;
        message({ message: msg, type: "error" });
        return Promise.reject(msg);
    }
  },
  (error) => {
    if (error?.name === "CanceledError") {
      console.log("Canceled Request:", error.message);
      // 请求取消是预期内行为（如同参数的新请求取消旧请求）。
      // 必须 resolve 一个可辨识的空结果而不是 undefined / reject：
      // - 不 reject：避免调用方无 catch 时产生大量 unhandled rejection 刷屏；
      // - 不返回 undefined：避免调用方在 .then(res => res.code) 中读取崩溃。
      return { code: 0, data: null, msg: "canceled", canceled: true };
    }
    handleError(error);
    return Promise.reject(error);
  }
);

/**
 *
 * @param {string} method
 * @param {string} url
 * @param {object} params
 * @param {boolean} noCancelable 默认 false，可以取消请求，若为 true 表示请求不可取消
 * @param {boolean} isFormData
 * @param {boolean} isPyRequest 标识 Python接口，Host默认 cn.
 * @param {boolean} isFPyRequest 传入 true，Python 请求 f.域名
 * @param {{signal?:AbortSignal}} requestOptions 调用方可传入信号，取消已失效的列表请求
 * @returns
 */
const axios_ = async (
  method,
  url,
  params = {},
  noCancelable = false,
  isFormData = false,
  isPyRequest = false,
  isFPyRequest = false,
  requestOptions = {}
) => {
  let aiHost = process.env.VUE_APP_AI_API_URL;
  if (isPyRequest) {
    if (isFPyRequest) {
      aiHost = process.env.VUE_APP_PY_F_API_URL;
    } else {
      aiHost = process.env.VUE_APP_PY_CN_API_URL;
    }
  }
  let config = {
    method,
    url: aiHost + url,
    ...(method.toLowerCase() === "get" ? { params } : { data: params }),
    noCancelable,
    // 显式传入的取消信号用于页面切换筛选时终止旧请求；未传时沿用全局去重逻辑。
    ...(requestOptions.signal ? { signal: requestOptions.signal } : {}),
  };

  if (isFormData) {
    config.headers = {
      "Content-Type": "multipart/form-data",
    };
  }

  try {
    const response = await service(config);
    return response?.data;
  } catch (error) {
    return await Promise.reject(error);
  }
};
export default axios_;
